import type {
    AttributeType,
    AttributeValue,
    ChildSelector,
    PropsAttribute,
} from './ui-element.type';
import type { CustomFormComponent } from '../types/custom-form-component';
import type { HTMLInputSelectOrTextElement } from '../global/utils.type';
import { UIElement } from './ui-element';
import { nameConverter } from '../global/render-api';

/*
 * Getter:
 * If no attribute attached to the element then the property value is false,
 * If the attribute equals either empty string (attribute without value)
 * or itself the property value is true
 *
 * Setter:
 * If value is true it will add an empty attribute
 * If value is false it will remove the attribute
 *
 * Sets the boolean descriptor compatible with HTML5 Specification
 * https://html.spec.whatwg.org/multipage/common-microsyntaxes.html#boolean-attributes
 */
const createBooleanDescriptor = <T extends UIElement>(
    attributeName: string,
    defaultValue: AttributeValue,
    reflect: boolean,
    sym: symbol
): PropertyDescriptor => ({
    get(this: T): AttributeValue {
        const hasAttribute = this.hasAttribute(attributeName);
        const value = this[sym];

        // If the value does not have initial value, then it is null,
        // also might be initialized as undefined if determined value is unknown yet
        // like in @Attribute decorator.
        // If there is no initialized value and no attribute exists, we assume
        // that value was not ever set.
        const hasInitialValue = typeof value === 'undefined' || value === null;
        if (!hasAttribute && hasInitialValue) {
            return Boolean(defaultValue);
        }
        return Boolean(hasAttribute || hasAttribute);
    },
    set(this: T, value): void {
        this[sym as keyof T] = value;
        if (!reflect) {
            return;
        }
        if ([null, false, undefined].includes(value)) {
            this.removeAttribute(attributeName);
        } else {
            this.setAttribute(attributeName, '');
        }
    },
});

/*
 * Getter:
 * Gets string value from the attribute, if attribute is not exist
 * the default value will be returned (if presented, otherwise - null)
 *
 * Setter:
 * Converts any value to string and sets it to the attribute
 */
const createStringDescriptor = <T extends UIElement>(
    attributeName: string,
    defaultValue: AttributeValue,
    reflect: boolean,
    sym: symbol
): PropertyDescriptor => ({
    get(this: T): AttributeValue {
        const value = this.getAttribute(attributeName);
        if (value !== null) {
            return value;
        }
        return (this[sym as keyof T] as AttributeValue) ?? defaultValue ?? null;
    },
    set(this: T, value): void {
        this[sym as keyof T] = value;
        if (!reflect) {
            return;
        }
        if (value === null) {
            this.removeAttribute(attributeName);
        } else {
            this.setAttribute(attributeName, value);
        }
    },
});

/*
 * Getter:
 * Gets number value from the attribute, if attribute is not exist
 * the default value will be returned (if presented, otherwise - null)
 *
 * Setter:
 * Sets string representation of number value to the attribute
 */
const createNumberDescriptor = <T extends UIElement>(
    attributeName: string,
    defaultValue: AttributeValue,
    reflect: boolean,
    sym: symbol
): PropertyDescriptor => ({
    get(this: T): AttributeValue {
        const value = this.getAttribute(attributeName);
        if (value !== null) {
            return parseFloat(value);
        }
        if (!reflect) {
            // For non-reflecting properties use the sym cache as the source of truth
            // with default fallbacks
            return this[sym] ?? defaultValue ?? 0;
        }
        return defaultValue !== undefined ? defaultValue : 0;
    },
    set(this: T, value): void {
        this[sym as keyof T] = value;
        if (!reflect) {
            return;
        }
        this.setAttribute(attributeName, String(value));
    },
});

/*
 * Getter:
 * This deals with objects/arrays, that are meant to be passed in via template literals or Angular.
 * "_..." in the attribute name is used in order to avoid infinite recursive getter calls.
 *
 * Setter:
 * Shallow equality is checked, so inorder to trigger the elements observedAttributes callback,
 * new object or array should be created with the updated structure.
 */
const createObjectDescriptor = <T extends UIElement>(
    attributeName: string,
    defaultValue: AttributeValue,
    reflect: boolean,
    sym: symbol
): PropertyDescriptor => ({
    get(this: T): AttributeValue | NonNullable<T[keyof T]> {
        return this[sym] || defaultValue;
    },
    set(this: T, value): void {
        this[sym as keyof T] = value;
        if (!reflect) {
            return;
        }
        const oldValue = this[sym];
        if (oldValue !== value) {
            this[sym as keyof T] = value;
            if (
                (
                    this.constructor as typeof UIElement
                ).observedAttributes.includes(attributeName)
            ) {
                this.observeAttributes(attributeName, oldValue, value);
            }
        }
    },
});

export const resolvePropertyDescriptor = (
    attributeName: string,
    type: AttributeType,
    defaultValue: AttributeValue,
    reflect: boolean
): PropertyDescriptor => {
    let descriptorCreator;
    const propertyName = nameConverter.attrToProperty(attributeName);
    const sym = Symbol.for(propertyName);
    switch (type) {
        case 'object':
        case Object:
            descriptorCreator = createObjectDescriptor;
            break;
        case 'boolean':
        case Boolean:
            descriptorCreator = createBooleanDescriptor;
            break;
        case 'number':
        case Number:
            descriptorCreator = createNumberDescriptor;
            break;
        case 'string':
        case String:
        default:
            descriptorCreator = createStringDescriptor;
            break;
    }
    const descriptor: PropertyDescriptor = descriptorCreator(
        attributeName,
        defaultValue,
        reflect,
        sym
    );
    descriptor.configurable = true;
    return descriptor;
};

export const resolveChildDescriptor = <T extends UIElement>(
    propertyName: string,
    selector: ChildSelector,
    multiple?: boolean
): PropertyDescriptor => {
    const sym = Symbol.for(propertyName);
    return {
        get(this: T): T | NodeListOf<T> | null {
            return this[sym] || !!multiple
                ? this.querySelectorAll<T>(selector)
                : this.querySelector<T>(selector);
        },
        set(this: T, value): void {
            this[sym as keyof T] = value;
        },
        configurable: true,
    };
};

/*
 * Create reflection: linkage between property and attribute.
 */
export const twoWayBinding = (
    constructor: typeof UIElement,
    attribute: PropsAttribute
): void => {
    // Skip aria and data attributes, these should act completely natively.
    if (
        ['aria-', 'data-'].some((prefix) =>
            String(attribute.name).startsWith(prefix)
        )
    ) {
        return;
    }
    const oldCallback = constructor.prototype.observeAttributes;
    constructor.prototype.observeAttributes = function (
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        const shouldChangeValue =
            attribute.name === name &&
            oldValue !== newValue &&
            attribute.reflect;
        if (shouldChangeValue) {
            const sym = Symbol.for(String(attribute.propertyName));
            this[sym] = newValue;
        }
        oldCallback.call(this, name, oldValue, newValue);
    };
};

export const isCustomFormComponent = (
    element: unknown
): element is CustomFormComponent => {
    if (!element || typeof element !== 'object') {
        return false;
    }

    const e = element as Record<string, unknown>;
    return (
        typeof e.getControlElement === 'function' &&
        typeof e.onValuePatched === 'function' &&
        typeof e.onDisabledStateChanged === 'function'
    );
};

const BOOLEAN_STATE_PROPERTIES_CALLBACK_MAP = {
    disabled: 'onDisabledStateChanged',
    checked: 'onCheckedStateChanged',
    selected: 'onSelectedStateChanged',
    readOnly: 'onReadonlyStateChanged',
} as const;

// private helper
const patchControlledValueProperty = (
    controlElement: HTMLInputSelectOrTextElement,
    onValuePatched: CustomFormComponent['onValuePatched']
): void => {
    const nativeDescriptor = Object.getOwnPropertyDescriptor(
        controlElement.constructor.prototype,
        'value'
    );
    if (!nativeDescriptor?.get || !nativeDescriptor?.set) {
        return;
    }
    Object.defineProperty(controlElement, 'value', {
        get(): AttributeValue {
            return nativeDescriptor.get!.call(controlElement) as AttributeValue;
        },
        set(newValue: AttributeValue): void {
            const prev = nativeDescriptor.get!.call(controlElement);
            nativeDescriptor.set!.call(controlElement, newValue);
            // Read back the actual value after the native set for <select> elements,
            // the browser silently resets to '' if newValue doesn't match any option.
            // Using the actual post-set value keeps the component in sync with reality.
            const actual = nativeDescriptor.get!.call(controlElement);
            if (prev === actual) {
                return;
            }
            // For <select> elements the browser silently resets value to '' when the
            // requested value doesn't match any <option>. Detect this by checking whether
            // the element is a select and the set produced '' from an empty newValue —
            // a sign that it was a framework init write (e.g. Angular writeValue(null))
            // that found no matching option. Propagating '' in that case would wrongly
            // reset a real user selection. For <input>/<textarea> this guard never fires
            // because setting '' on those always intentionally produces '' and is a valid
            // clear that should be propagated.
            const isSelect = controlElement instanceof HTMLSelectElement;
            const newValueIsEmpty =
                newValue === null || newValue === undefined || newValue === '';
            if (isSelect && newValueIsEmpty && actual === '') {
                return;
            }
            onValuePatched(actual as AttributeValue);
        },
        configurable: true,
    });
};

// private helper
const patchControlledBooleanStateProperty = (
    controlElement: HTMLElement,
    property: string,
    onStateChanged: (newState: boolean) => void
): void => {
    const nativeDescriptor = Object.getOwnPropertyDescriptor(
        controlElement.constructor.prototype,
        property
    );
    if (!nativeDescriptor?.get || !nativeDescriptor?.set) {
        return;
    }

    Object.defineProperty(controlElement, property, {
        get(): boolean {
            return nativeDescriptor.get!.call(controlElement) as boolean;
        },
        set(newValue: boolean): void {
            const prev = nativeDescriptor.get!.call(controlElement);
            nativeDescriptor.set!.call(controlElement, newValue);
            if (prev !== newValue) {
                onStateChanged(newValue);
            }
        },
        configurable: true,
    });
};

export const patchExternallyControlledFormProperties = (
    uiElement: UIElement,
    patchedControlElements: WeakSet<HTMLElement>
): void => {
    if (!isCustomFormComponent(uiElement)) {
        return;
    }

    const controlElement = uiElement.getControlElement();
    if (!controlElement) {
        return;
    }
    if (patchedControlElements.has(controlElement)) {
        return;
    }
    patchedControlElements.add(controlElement);

    // value
    patchControlledValueProperty(
        controlElement,
        uiElement.onValuePatched.bind(uiElement)
    );

    // booleans
    Object.entries(BOOLEAN_STATE_PROPERTIES_CALLBACK_MAP).forEach(
        ([property, callback]) => {
            if (typeof uiElement[callback] === 'function') {
                patchControlledBooleanStateProperty(
                    controlElement,
                    property,
                    uiElement[callback]?.bind(uiElement)
                );
            }
        }
    );
};
